import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { publicAPI } from '../services/api';
import { useIntersect } from '../hooks/useIntersect';

const MEDIA_URL = process.env.REACT_APP_MEDIA_URL || 'http://localhost:5001';
const API_URL   = process.env.REACT_APP_API_URL   || 'http://localhost:5002/api/v1';
const LMS_URL   = process.env.REACT_APP_LMS_URL   || 'http://localhost:3002';

const TYPE_COLORS = {
  bootcamp:     '#e8a43a',
  workshop:     '#4e9cf5',
  certification:'#3ecf8e',
  course:       '#a78bfa',
  mentorship:   '#f472b6',
};

const MODE_LABELS = { in_person: 'In-Person', remote: 'Remote', hybrid: 'Hybrid' };

function resolveMedia(url) {
  if (!url) return null;
  return url.startsWith('http') ? url : `${MEDIA_URL}${url}`;
}

async function fetchCurrentUser() {
  const token = localStorage.getItem('spa_token');
  if (!token) return null;
  try {
    const res = await fetch(`${API_URL}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.data || data.user || data;
  } catch { return null; }
}

// ── Smart Enroll Modal ────────────────────────────────────
// ── Smart Enrollment Modal ────────────────────────────────
// Drop this into both ProgramDetailPage.jsx and ProgramsPage.jsx
// Replace the entire EnrollModal function with this one

const API_URL_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5002/api/v1';

// Pass token to LMS so learner doesn't need to log in again
const getLMSUrl = () => {
  const token = localStorage.getItem('spa_token');
  const base  = process.env.REACT_APP_LMS_URL || 'http://localhost:3002';
  return token ? `${base}/auth?token=${encodeURIComponent(token)}` : `${base}/login`;
};

// Fetch logged-in user profile
async function fetchLoggedInUser() {
  const token = localStorage.getItem('spa_token');
  if (!token) return null;
  try {
    const res = await fetch(`${API_URL_BASE}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.data || data.user || data;
  } catch { return null; }
}

// Rwanda phone validation: 07x or +2507x, 10 digits local / 12 international
function validatePhone(p) {
  if (!p) return true; // phone is optional
  const cleaned = p.replace(/[\s\-()]/g, '');
  return /^(\+250|0)(7[2-9])\d{7}$/.test(cleaned);
}

function validateEmail(e) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
}

// ── Step indicators ───────────────────────────────────────
function Steps({ step, total }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:6, padding:'0 32px 20px' }}>
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} style={{ display:'flex', alignItems:'center', gap:6, flex: i < total - 1 ? 1 : 0 }}>
          <div style={{
            width:24, height:24, borderRadius:'50%', display:'flex', alignItems:'center',
            justifyContent:'center', fontSize:'0.7rem', fontWeight:700, flexShrink:0,
            background: i < step ? 'var(--teal)' : i === step ? 'var(--gold)' : 'var(--bg-3)',
            color: i <= step ? '#080a0d' : 'var(--t-muted)',
            border: `1px solid ${i < step ? 'var(--teal)' : i === step ? 'var(--gold)' : 'var(--border)'}`,
          }}>
            {i < step ? '✓' : i + 1}
          </div>
          {i < total - 1 && (
            <div style={{ flex:1, height:1, background: i < step ? 'var(--teal)' : 'var(--border)' }}/>
          )}
        </div>
      ))}
    </div>
  );
}

function EnrollModal({ program, cohort, onClose }) {
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [step, setStep] = useState(0); // 0=personal, 1=academic, 2=confirm
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const [errMsg, setErrMsg] = useState('');

  // Personal info
  const [personal, setPersonal] = useState({
    full_name: '', email: '', phone: '', location: 'Gisenyi, Rubavu',
  });

  // Academic/application info  
  const [academic, setAcademic] = useState({
    experience_level: 'beginner',
    education_level:  'secondary',
    current_occupation: '',
    motivation: '',
    goals: '',
    hear_about_us: '',
    emergency_contact: '',
  });

  const [saveProfile, setSaveProfile] = useState(true);

  const setP = (k, v) => { setPersonal(p => ({ ...p, [k]: v })); setErrors(e => ({ ...e, [k]: '' })); };
  const setA = (k, v) => setAcademic(a => ({ ...a, [k]: v }));

  // Load user on mount
  useEffect(() => {
    fetchLoggedInUser().then(u => {
      setLoggedInUser(u);
      if (u) {
        setPersonal(p => ({
          ...p,
          full_name: u.name || u.full_name || '',
          email:     u.email || '',
          phone:     u.phone || '',
        }));
      }
      setChecking(false);
    });
  }, []);

  // ── Validation ────────────────────────────────────────
  const validateStep0 = () => {
    const errs = {};
    if (!personal.full_name?.trim())     errs.full_name = 'Full name is required';
    else if (personal.full_name.trim().split(' ').length < 2) errs.full_name = 'Please enter your full name (first and last)';
    if (!personal.email?.trim())         errs.email = 'Email is required';
    else if (!validateEmail(personal.email)) errs.email = 'Please enter a valid email address';
    if (personal.phone && !validatePhone(personal.phone)) errs.phone = 'Enter a valid Rwanda number (e.g. 0781234567)';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateStep1 = () => {
    const errs = {};
    if (!academic.motivation?.trim() || academic.motivation.trim().length < 20)
      errs.motivation = 'Please tell us more about your motivation (at least 20 characters)';
    if (!academic.goals?.trim() || academic.goals.trim().length < 20)
      errs.goals = 'Please describe your goals (at least 20 characters)';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => {
    if (step === 0 && !validateStep0()) return;
    if (step === 1 && !validateStep1()) return;
    setStep(s => s + 1);
  };

  // ── Submit ────────────────────────────────────────────
  const submit = async () => {
    setStatus('loading');
    try {
      const token = localStorage.getItem('spa_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // Save profile if requested
      if (saveProfile && token) {
        await fetch(`${API_URL_BASE}/auth/profile`, {
          method: 'PUT',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name:  personal.full_name,
            phone: personal.phone,
          }),
        }).catch(() => {});
      }

      // Submit enrollment
      const res = await fetch(`${API_URL_BASE}/programs/enroll`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          program_id:         program.id,
          cohort_id:          cohort?.id || program.active_cohort_id || null,
          full_name:          personal.full_name,
          email:              personal.email,
          phone:              personal.phone,
          experience_level:   academic.experience_level,
          education_level:    academic.education_level,
          current_occupation: academic.current_occupation,
          motivation:         academic.motivation,
          goals:              academic.goals,
          hear_about_us:      academic.hear_about_us,
          emergency_contact:  academic.emergency_contact,
          location:           personal.location,
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Enrollment failed');
      }
      setStatus('success');
    } catch (err) {
      setErrMsg(err.message || 'Enrollment failed. Please try again.');
      setStatus('error');
    }
  };

  // ── Field components ──────────────────────────────────
  const Field = ({ label, error, required, children }) => (
    <div>
      <label style={{ display:'block', fontSize:'0.75rem', color: error ? 'var(--red)' : 'var(--t-secondary)', fontWeight:500, textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:6 }}>
        {label}{required && <span style={{ color:'var(--red)', marginLeft:3 }}>*</span>}
      </label>
      {children}
      {error && <div style={{ fontSize:'0.75rem', color:'var(--red)', marginTop:4 }}>⚠ {error}</div>}
    </div>
  );

  const inputStyle = (hasError) => ({
    width:'100%', background:'var(--bg-2)',
    border:`1px solid ${hasError ? 'rgba(240,96,96,0.5)' : 'var(--border)'}`,
    borderRadius:'var(--r-md)', color:'var(--t-primary)',
    padding:'10px 13px', fontSize:'0.88rem', outline:'none',
    transition:'border-color 0.15s', boxSizing:'border-box',
  });

  const taStyle = (hasError) => ({ ...inputStyle(hasError), resize:'vertical', minHeight:90, fontFamily:'inherit' });

  // ── Not logged in ─────────────────────────────────────
  if (!checking && !loggedInUser && status !== 'success') return (
    <Overlay onClose={onClose}>
      <ModalHeader program={program} cohort={cohort} onClose={onClose}/>
      <div style={{ padding:'40px 32px', textAlign:'center' }}>
        <div style={{ fontSize:'2.5rem', marginBottom:16 }}>🔐</div>
        <h3 style={{ fontFamily:'var(--font-display)', fontSize:'1.2rem', marginBottom:8 }}>Sign in to enroll</h3>
        <p style={{ color:'var(--t-secondary)', marginBottom:28, lineHeight:1.7, fontSize:'0.9rem' }}>
          Create a free MilleHills account to enroll, track your progress, and earn certificates.
        </p>
        <div style={{ display:'flex', flexDirection:'column', gap:10, maxWidth:260, margin:'0 auto' }}>
          <a href={`/login?redirect=/programs/${program.slug}`} className="btn btn-primary" style={{ justifyContent:'center', fontWeight:600, padding:'13px' }}>Sign In →</a>
          <a href={`/login?tab=register&redirect=/programs/${program.slug}`} className="btn btn-outline" style={{ justifyContent:'center', padding:'13px' }}>Create Account</a>
        </div>
      </div>
    </Overlay>
  );

  // ── Success ───────────────────────────────────────────
  if (status === 'success') return (
    <Overlay onClose={onClose}>
      <div style={{ padding:'40px 32px', textAlign:'center' }}>
        <div style={{ width:60, height:60, borderRadius:'50%', background:'rgba(62,207,142,0.15)', border:'1px solid rgba(62,207,142,0.3)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px', fontSize:'1.8rem' }}>🎉</div>
        <h3 style={{ fontFamily:'var(--font-display)', fontSize:'1.4rem', color:'var(--gold)', marginBottom:10 }}>You're enrolled!</h3>
        <p style={{ color:'var(--t-secondary)', marginBottom:8, lineHeight:1.7, fontSize:'0.9rem' }}>
          Welcome to <strong>{program.title}</strong>, {personal.full_name.split(' ')[0]}!
        </p>
        <p style={{ color:'var(--t-muted)', fontSize:'0.82rem', marginBottom:28, lineHeight:1.6 }}>
          A confirmation email has been sent to <strong>{personal.email}</strong>.
        </p>
        <div style={{ display:'flex', flexDirection:'column', gap:10, maxWidth:280, margin:'0 auto' }}>
          <a href={getLMSUrl()} target="_blank" rel="noopener noreferrer"
            className="btn btn-primary" style={{ justifyContent:'center', fontWeight:600, padding:'13px' }}>
            🎓 Start Learning Now →
          </a>
          <button onClick={onClose} className="btn btn-outline" style={{ justifyContent:'center' }}>Close</button>
        </div>
      </div>
    </Overlay>
  );

  if (checking) return (
    <Overlay onClose={onClose}>
      <div style={{ padding:48, textAlign:'center', color:'var(--t-muted)' }}>Checking your account…</div>
    </Overlay>
  );

  // ── Multi-step form ───────────────────────────────────
  return (
    <Overlay onClose={onClose}>
      <ModalHeader program={program} cohort={cohort} onClose={onClose}/>
      <Steps step={step} total={3}/>

      {/* STEP 0 — Personal info */}
      {step === 0 && (
        <div style={{ padding:'0 32px 28px', display:'flex', flexDirection:'column', gap:14 }}>
          <div style={{ fontSize:'0.82rem', color:'var(--t-muted)', marginBottom:4 }}>
            Step 1 of 3 — Personal Information
          </div>

          <Field label="Full Name" error={errors.full_name} required>
            <input value={personal.full_name} onChange={e=>setP('full_name',e.target.value)}
              placeholder="e.g. Jean Claude Habimana" style={inputStyle(errors.full_name)}
              onFocus={e=>e.target.style.borderColor='var(--gold-border)'}
              onBlur={e=>e.target.style.borderColor=errors.full_name?'rgba(240,96,96,0.5)':'var(--border)'}/>
          </Field>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            <Field label="Email" error={errors.email} required>
              <input type="email" value={personal.email} onChange={e=>setP('email',e.target.value)}
                placeholder="you@email.com" style={inputStyle(errors.email)}
                onFocus={e=>e.target.style.borderColor='var(--gold-border)'}
                onBlur={e=>e.target.style.borderColor=errors.email?'rgba(240,96,96,0.5)':'var(--border)'}/>
            </Field>
            <Field label="Phone (Rwanda)" error={errors.phone}>
              <input value={personal.phone} onChange={e=>setP('phone',e.target.value)}
                placeholder="0781234567" style={inputStyle(errors.phone)}
                onFocus={e=>e.target.style.borderColor='var(--gold-border)'}
                onBlur={e=>e.target.style.borderColor=errors.phone?'rgba(240,96,96,0.5)':'var(--border)'}/>
              {personal.phone && !errors.phone && validatePhone(personal.phone) && (
                <div style={{ fontSize:'0.72rem', color:'var(--teal)', marginTop:3 }}>✓ Valid number</div>
              )}
            </Field>
          </div>

          <Field label="Location">
            <input value={personal.location} onChange={e=>setP('location',e.target.value)}
              placeholder="City, District" style={inputStyle(false)}
              onFocus={e=>e.target.style.borderColor='var(--gold-border)'}
              onBlur={e=>e.target.style.borderColor='var(--border)'}/>
          </Field>

          {loggedInUser && (
            <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:'0.82rem', cursor:'pointer', color:'var(--t-secondary)' }}>
              <input type="checkbox" checked={saveProfile} onChange={e=>setSaveProfile(e.target.checked)}/>
              Save this information to my profile for future applications
            </label>
          )}

          <button onClick={handleNext} className="btn btn-primary" style={{ justifyContent:'center', padding:'12px', marginTop:4, fontWeight:600 }}>
            Next: Academic Background →
          </button>
        </div>
      )}

      {/* STEP 1 — Academic / Application */}
      {step === 1 && (
        <div style={{ padding:'0 32px 28px', display:'flex', flexDirection:'column', gap:14 }}>
          <div style={{ fontSize:'0.82rem', color:'var(--t-muted)', marginBottom:4 }}>
            Step 2 of 3 — Academic Application
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            <Field label="Experience Level">
              <select value={academic.experience_level} onChange={e=>setA('experience_level',e.target.value)} style={inputStyle(false)}>
                <option value="beginner">Beginner — No prior experience</option>
                <option value="intermediate">Intermediate — Some experience</option>
                <option value="advanced">Advanced — Significant experience</option>
              </select>
            </Field>
            <Field label="Highest Education">
              <select value={academic.education_level} onChange={e=>setA('education_level',e.target.value)} style={inputStyle(false)}>
                <option value="primary">Primary School</option>
                <option value="secondary">Secondary School (S6/O-Level)</option>
                <option value="tvet">TVET / Vocational</option>
                <option value="diploma">Diploma / A-Level</option>
                <option value="degree">Bachelor's Degree</option>
                <option value="postgrad">Postgraduate</option>
              </select>
            </Field>
          </div>

          <Field label="Current Occupation">
            <input value={academic.current_occupation} onChange={e=>setA('current_occupation',e.target.value)}
              placeholder="e.g. Student, Teacher, Freelancer, Unemployed"
              style={inputStyle(false)}
              onFocus={e=>e.target.style.borderColor='var(--gold-border)'}
              onBlur={e=>e.target.style.borderColor='var(--border)'}/>
          </Field>

          <Field label="Why do you want to join this program?" error={errors.motivation} required>
            <textarea value={academic.motivation} onChange={e=>setA('motivation',e.target.value)}
              placeholder="Tell us what motivated you to apply and why this program is right for you…"
              style={taStyle(errors.motivation)}
              onFocus={e=>e.target.style.borderColor='var(--gold-border)'}
              onBlur={e=>e.target.style.borderColor=errors.motivation?'rgba(240,96,96,0.5)':'var(--border)'}/>
            <div style={{ fontSize:'0.7rem', color: academic.motivation.length < 20 ? 'var(--t-muted)' : 'var(--teal)', marginTop:3, textAlign:'right' }}>
              {academic.motivation.length} chars {academic.motivation.length < 20 ? `(${20-academic.motivation.length} more needed)` : '✓'}
            </div>
          </Field>

          <Field label="What do you hope to achieve?" error={errors.goals} required>
            <textarea value={academic.goals} onChange={e=>setA('goals',e.target.value)}
              placeholder="Describe your learning goals and what you plan to do after completing this program…"
              style={taStyle(errors.goals)}
              onFocus={e=>e.target.style.borderColor='var(--gold-border)'}
              onBlur={e=>e.target.style.borderColor=errors.goals?'rgba(240,96,96,0.5)':'var(--border)'}/>
            <div style={{ fontSize:'0.7rem', color: academic.goals.length < 20 ? 'var(--t-muted)' : 'var(--teal)', marginTop:3, textAlign:'right' }}>
              {academic.goals.length} chars {academic.goals.length < 20 ? `(${20-academic.goals.length} more needed)` : '✓'}
            </div>
          </Field>

          <Field label="How did you hear about us?">
            <select value={academic.hear_about_us} onChange={e=>setA('hear_about_us',e.target.value)} style={inputStyle(false)}>
              <option value="">— Select —</option>
              <option value="social_media">Social Media (Facebook, Instagram, X)</option>
              <option value="friend">Friend or Colleague</option>
              <option value="school">School or University</option>
              <option value="google">Google Search</option>
              <option value="event">Event or Workshop</option>
              <option value="other">Other</option>
            </select>
          </Field>

          <Field label="Emergency Contact (optional)">
            <input value={academic.emergency_contact} onChange={e=>setA('emergency_contact',e.target.value)}
              placeholder="Name and phone number of someone to contact if needed"
              style={inputStyle(false)}
              onFocus={e=>e.target.style.borderColor='var(--gold-border)'}
              onBlur={e=>e.target.style.borderColor='var(--border)'}/>
          </Field>

          <div style={{ display:'flex', gap:10 }}>
            <button onClick={()=>setStep(0)} className="btn btn-outline" style={{ justifyContent:'center', flex:1, padding:'12px' }}>← Back</button>
            <button onClick={handleNext} className="btn btn-primary" style={{ justifyContent:'center', flex:2, padding:'12px', fontWeight:600 }}>
              Review Application →
            </button>
          </div>
        </div>
      )}

      {/* STEP 2 — Review & confirm */}
      {step === 2 && (
        <div style={{ padding:'0 32px 28px', display:'flex', flexDirection:'column', gap:16 }}>
          <div style={{ fontSize:'0.82rem', color:'var(--t-muted)', marginBottom:4 }}>
            Step 3 of 3 — Review & Confirm
          </div>

          {/* Summary card */}
          <div style={{ background:'var(--bg-3)', borderRadius:'var(--r-lg)', border:'1px solid var(--border)', overflow:'hidden' }}>
            <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--border)', fontFamily:'var(--font-display)', fontWeight:600, fontSize:'0.85rem', color:'var(--gold)' }}>
              Application Summary
            </div>
            <div style={{ padding:'14px 16px', display:'flex', flexDirection:'column', gap:8 }}>
              {[
                ['Program',    program.title],
                ['Name',       personal.full_name],
                ['Email',      personal.email],
                ['Phone',      personal.phone || '—'],
                ['Location',   personal.location || '—'],
                ['Experience', academic.experience_level],
                ['Education',  academic.education_level],
                ['Occupation', academic.current_occupation || '—'],
              ].map(([k,v]) => (
                <div key={k} style={{ display:'flex', gap:12, fontSize:'0.82rem' }}>
                  <span style={{ color:'var(--t-muted)', width:90, flexShrink:0 }}>{k}</span>
                  <span style={{ color:'var(--t-primary)', fontWeight:500 }}>{v}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Motivation preview */}
          <div style={{ background:'var(--bg-3)', borderRadius:'var(--r-md)', padding:'12px 14px', border:'1px solid var(--border)' }}>
            <div style={{ fontSize:'0.7rem', color:'var(--t-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>Motivation</div>
            <div style={{ fontSize:'0.83rem', color:'var(--t-secondary)', lineHeight:1.65 }}>{academic.motivation}</div>
          </div>

          {status === 'error' && (
            <div style={{ padding:'12px 14px', background:'rgba(240,96,96,0.1)', border:'1px solid rgba(240,96,96,0.3)', borderRadius:'var(--r-md)', fontSize:'0.84rem', color:'var(--red)' }}>
              ⚠ {errMsg}
            </div>
          )}

          <div style={{ display:'flex', gap:10 }}>
            <button onClick={()=>setStep(1)} className="btn btn-outline" style={{ justifyContent:'center', flex:1, padding:'12px' }}>← Edit</button>
            <button onClick={submit} disabled={status==='loading'} className="btn btn-primary"
              style={{ justifyContent:'center', flex:2, padding:'12px', fontWeight:600 }}>
              {status==='loading' ? 'Submitting…' : '✓ Confirm Enrollment'}
            </button>
          </div>

          <div style={{ fontSize:'0.75rem', color:'var(--t-muted)', textAlign:'center', lineHeight:1.6 }}>
            By enrolling you agree to MilleHills terms. Your data is used only for program administration.
          </div>
        </div>
      )}
    </Overlay>
  );
}

// ── Helper sub-components ─────────────────────────────────
function Overlay({ onClose, children }) {
  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:600, background:'rgba(0,0,0,0.85)', backdropFilter:'blur(8px)', display:'flex', alignItems:'center', justifyContent:'center', padding:20, overflowY:'auto' }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:'var(--bg-1)', border:'1px solid var(--border)', borderRadius:'var(--r-xl)', width:'100%', maxWidth:560, maxHeight:'92vh', overflow:'auto', animation:'scaleIn 0.25s ease' }}>
        {children}
      </div>
    </div>
  );
}

function ModalHeader({ program, cohort, onClose }) {
  return (
    <div style={{ padding:'24px 32px 16px', display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
      <div>
        <div style={{ fontSize:'0.68rem', color:'var(--t-muted)', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:4 }}>Enrollment Application</div>
        <h2 style={{ fontFamily:'var(--font-display)', fontSize:'1.05rem', fontWeight:600, lineHeight:1.3 }}>{program.title}</h2>
        {cohort && <div style={{ fontSize:'0.78rem', color:'var(--gold)', marginTop:3 }}>Cohort: {cohort.cohort_name}</div>}
      </div>
      <button onClick={onClose} style={{ color:'var(--t-muted)', fontSize:'1.2rem', padding:4, lineHeight:1, background:'none', border:'none', cursor:'pointer', flexShrink:0 }}>✕</button>
    </div>
  );
}

// ── Program Card ──────────────────────────────────────────
function ProgramCard({ program, onEnroll, idx }) {
  const [ref, visible] = useIntersect();
  const color = TYPE_COLORS[program.type] || 'var(--gold)';
  const src   = resolveMedia(program.media_url || program.thumbnail_url);

  return (
    <div ref={ref} className="card" style={{
      opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(24px)',
      transition: `opacity 0.5s ${idx*0.06}s, transform 0.5s ${idx*0.06}s`,
      display:'flex', flexDirection:'column',
      borderLeft: src ? 'none' : `3px solid ${color}`, padding:0, overflow:'hidden',
    }}>
      {src && (
        <div style={{ height:150, position:'relative', flexShrink:0 }}>
          <img src={src} alt={program.title} style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} onError={e=>{e.target.parentNode.style.display='none'}}/>
          <div style={{ position:'absolute', inset:0, background:`linear-gradient(to top, ${color}33 0%, transparent 60%)` }}/>
          <span className="badge" style={{ position:'absolute', top:12, left:12, background:`${color}cc`, color:'#080a0d', border:'none', fontSize:'0.62rem', backdropFilter:'blur(4px)' }}>{program.type}</span>
          {program.is_free && <span className="badge" style={{ position:'absolute', top:12, right:12, background:'rgba(62,207,142,0.85)', color:'#080a0d', border:'none', fontSize:'0.62rem' }}>Free</span>}
        </div>
      )}
      <div style={{ padding:'20px 24px 24px', display:'flex', flexDirection:'column', gap:14, flex:1, borderLeft: src ? `3px solid ${color}` : 'none' }}>
        {!src && (
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            <span className="badge" style={{ background:`${color}15`, color, border:`1px solid ${color}44`, fontSize:'0.62rem' }}>{program.type}</span>
            {program.delivery_mode && <span className="badge badge-dim">{program.delivery_mode}</span>}
            {program.is_free && <span className="badge" style={{ background:'rgba(62,207,142,0.12)', color:'#3ecf8e', border:'1px solid rgba(62,207,142,0.3)' }}>Free</span>}
          </div>
        )}
        <div>
          <h3 style={{ fontFamily:'var(--font-display)', fontSize:'1.05rem', fontWeight:600, lineHeight:1.3, marginBottom:8 }}>{program.title}</h3>
          {program.domain_name && <div style={{ fontSize:'0.75rem', color:'var(--t-muted)', letterSpacing:'0.05em', textTransform:'uppercase', marginBottom:8 }}>{program.domain_name}</div>}
          <p className="body-sm" style={{ color:'var(--t-secondary)', lineHeight:1.65 }}>{program.summary?.slice(0,130)}{program.summary?.length>130?'…':''}</p>
        </div>
        <div style={{ display:'flex', gap:16, flexWrap:'wrap', marginTop:'auto' }}>
          {program.duration && <div><div className="label" style={{ color:'var(--t-muted)', fontSize:'0.6rem' }}>Duration</div><div style={{ fontSize:'0.85rem', fontWeight:500 }}>{program.duration}</div></div>}
          {program.next_cohort_date && <div><div className="label" style={{ color:'var(--t-muted)', fontSize:'0.6rem' }}>Next Cohort</div><div style={{ fontSize:'0.85rem', fontWeight:500, color }}>{new Date(program.next_cohort_date).toLocaleDateString('en-RW',{month:'short',day:'numeric',year:'numeric'})}</div></div>}
        </div>
        <div style={{ display:'flex', gap:8, marginTop:4 }}>
          <Link to={`/programs/${program.slug}`} className="btn btn-outline" style={{ flex:1, justifyContent:'center', fontSize:'0.82rem' }}>
            View Details
          </Link>
          <button onClick={()=>onEnroll(program)} className="btn btn-primary" style={{ flex:1, justifyContent:'center', background:color, color:'#080a0d', fontSize:'0.82rem' }}>
            {program.status==='open'||program.status==='ongoing' ? 'Enroll' : 'Interest'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Internship Card ───────────────────────────────────────
function InternshipCard({ internship, idx }) {
  const [ref, visible] = useIntersect();
  const [hover, setHover] = useState(false);
  const src    = resolveMedia(internship.media_url);
  const isOpen = internship.status === 'open';

  return (
    <Link ref={ref} to={`/internships/${internship.slug}`}
      onMouseEnter={()=>setHover(true)} onMouseLeave={()=>setHover(false)}
      className="card" style={{
        padding:0, display:'flex', flexDirection:'column', overflow:'hidden',
        opacity:visible?1:0, transform:visible?'none':'translateY(24px)',
        transition:`opacity 0.5s ${idx*0.06}s, transform 0.5s ${idx*0.06}s`,
        textDecoration:'none', borderLeft:src?'none':'3px solid var(--gold)',
      }}>
      {src && (
        <div style={{ height:140, overflow:'hidden', position:'relative', flexShrink:0 }}>
          <img src={src} alt={internship.title} style={{ width:'100%', height:'100%', objectFit:'cover', transform:hover?'scale(1.04)':'scale(1)', transition:'transform 0.4s' }}/>
          <div style={{ position:'absolute', inset:0, background:'linear-gradient(to top, rgba(8,10,13,0.7) 0%, transparent 60%)' }}/>
          <span style={{ position:'absolute', top:10, left:10, fontSize:'0.6rem', fontWeight:700, padding:'3px 8px', borderRadius:20, background:isOpen?'rgba(62,207,142,0.85)':'rgba(80,80,80,0.85)', color:'#fff' }}>
            {isOpen ? 'Open' : internship.status?.replace('_',' ')}
          </span>
          {internship.is_paid && <span style={{ position:'absolute', top:10, right:10, fontSize:'0.6rem', fontWeight:700, padding:'3px 8px', borderRadius:20, background:'rgba(232,164,58,0.9)', color:'#080a0d' }}>Paid</span>}
        </div>
      )}
      <div style={{ padding:'18px 22px 22px', display:'flex', flexDirection:'column', gap:12, flex:1 }}>
        {!src && (
          <div style={{ display:'flex', gap:8 }}>
            <span className="badge badge-dim" style={{ fontSize:'0.6rem' }}>{internship.department||'Internship'}</span>
            <span className="badge" style={{ fontSize:'0.6rem', background:isOpen?'rgba(62,207,142,0.12)':'var(--bg-2)', color:isOpen?'#3ecf8e':'var(--t-muted)', border:`1px solid ${isOpen?'rgba(62,207,142,0.3)':'var(--border)'}` }}>{isOpen?'Open':internship.status?.replace('_',' ')}</span>
          </div>
        )}
        <h3 style={{ fontFamily:'var(--font-display)', fontSize:'1rem', fontWeight:600, lineHeight:1.3, color:hover?'var(--gold)':'var(--t-primary)', transition:'color 0.15s' }}>{internship.title}</h3>
        {internship.description && <p className="body-sm" style={{ color:'var(--t-secondary)', lineHeight:1.65 }}>{internship.description.slice(0,110)}{internship.description.length>110?'…':''}</p>}
        <div style={{ display:'flex', gap:16, flexWrap:'wrap', marginTop:'auto', paddingTop:12, borderTop:'1px solid var(--border)' }}>
          {internship.duration && <div><div style={{ fontSize:'0.6rem', color:'var(--t-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:2 }}>Duration</div><div style={{ fontSize:'0.82rem', fontWeight:500 }}>{internship.duration}</div></div>}
          {internship.mode && <div><div style={{ fontSize:'0.6rem', color:'var(--t-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:2 }}>Mode</div><div style={{ fontSize:'0.82rem', fontWeight:500 }}>{MODE_LABELS[internship.mode]||internship.mode}</div></div>}
          {internship.is_paid && <div><div style={{ fontSize:'0.6rem', color:'var(--t-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:2 }}>Stipend</div><div style={{ fontSize:'0.82rem', fontWeight:500, color:'var(--gold)' }}>RWF {Number(internship.stipend_amount||0).toLocaleString()}</div></div>}
          <div style={{ marginLeft:'auto' }}><span style={{ fontSize:'0.82rem', color:'var(--gold)', fontWeight:600 }}>Apply →</span></div>
        </div>
      </div>
    </Link>
  );
}

// ── Main Page ─────────────────────────────────────────────
export default function ProgramsPage() {
  const [searchParams] = useSearchParams();
  const [tab, setTab]             = useState(searchParams.get('tab') || 'programs');
  const [programs, setPrograms]   = useState([]);
  const [internships, setInterns] = useState([]);
  const [domains, setDomains]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [activeDomain, setDomain] = useState('');
  const [activeType, setType]     = useState(searchParams.get('type') || '');
  const [enrolling, setEnrolling] = useState(null);

  useEffect(() => {
    publicAPI.programDomains().then(r => setDomains(r.data?.data || [])).catch(() => {});
  }, []);

  const loadPrograms = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeDomain) params.domain = activeDomain;
      if (activeType)   params.type   = activeType;
      const res = await publicAPI.programs(params);
      setPrograms(res.data?.data || []);
    } catch { setPrograms([]); } finally { setLoading(false); }
  }, [activeDomain, activeType]);

  const loadInternships = useCallback(async () => {
    setLoading(true);
    try {
      const res = await publicAPI.internships({ status: 'open' });
      setInterns(res.data?.data || []);
    } catch { setInterns([]); } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (tab === 'programs') loadPrograms();
    else loadInternships();
  }, [tab, loadPrograms, loadInternships]);

  const TYPES = ['bootcamp','workshop','certification','course','mentorship'];

  const tabStyle = (t) => ({
    padding:'12px 24px', fontSize:'0.9rem', cursor:'pointer',
    background:'none', border:'none',
    borderBottom:`2px solid ${tab===t?'var(--gold)':'transparent'}`,
    color: tab===t?'var(--gold)':'var(--t-secondary)',
    fontWeight: tab===t?700:400,
    fontFamily: tab===t?'var(--font-display)':'inherit',
    transition:'all 0.15s', marginBottom:-1,
  });

  return (
    <div>
      {/* Hero */}
      <section style={{ paddingTop:'calc(var(--nav-h) + clamp(60px,8vw,100px))', paddingBottom:'clamp(60px,8vw,100px)', background:'linear-gradient(160deg, var(--bg-1) 0%, var(--bg-0) 70%)', position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', top:0, right:0, width:'50%', height:'100%', background:'linear-gradient(135deg, transparent 40%, rgba(232,164,58,0.04) 100%)', pointerEvents:'none' }}/>
        <div className="container" style={{ position:'relative', zIndex:1 }}>
          <div className="section-label label" style={{ marginBottom:20 }}>Learn & Grow</div>
          <h1 className="display-lg" style={{ marginBottom:16 }}>
            Learn. Build.<br/><span style={{ color:'var(--gold)' }}>Lead. Intern.</span>
          </h1>
          <p className="body-lg" style={{ color:'var(--t-secondary)', maxWidth:520, marginBottom:32 }}>
            Structured training programs and hands-on internships — built for Rwanda's next generation of innovators.
          </p>
          <div style={{ display:'flex', gap:14, flexWrap:'wrap' }}>
            {[{label:'Bootcamps',icon:'⚡',color:'var(--gold)'},{label:'Workshops',icon:'◐',color:'#4e9cf5'},{label:'Certifications',icon:'◈',color:'#3ecf8e'},{label:'Internships',icon:'◉',color:'#f472b6'}].map(t => (
              <div key={t.label} style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 16px', background:'var(--bg-2)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', fontSize:'0.85rem', color:'var(--t-secondary)' }}>
                <span style={{ color:t.color }}>{t.icon}</span>{t.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tab bar + filters */}
      <div style={{ background:'var(--bg-0)', borderBottom:'1px solid var(--border)', position:'sticky', top:'var(--nav-h)', zIndex:50 }}>
        <div className="container" style={{ display:'flex', flexDirection:'column', gap:0 }}>
          <div style={{ display:'flex', gap:0 }}>
            <button style={tabStyle('programs')}    onClick={()=>setTab('programs')}>Training Programs</button>
            <button style={tabStyle('internships')} onClick={()=>setTab('internships')}>
              Internships
              <span style={{ marginLeft:6, fontSize:'0.65rem', padding:'2px 6px', borderRadius:10, background:'rgba(62,207,142,0.15)', color:'#3ecf8e', fontWeight:700 }}>Hiring</span>
            </button>
          </div>
          {tab === 'programs' && (
            <div style={{ padding:'10px 0 12px', display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
              {['', ...TYPES].map(t => (
                <button key={t} onClick={()=>setType(t)} style={{ padding:'6px 12px', borderRadius:20, fontSize:'0.75rem', cursor:'pointer', textTransform:'capitalize', fontWeight:activeType===t?700:400, background:activeType===t?(TYPE_COLORS[t]||'var(--gold)'):'var(--bg-2)', color:activeType===t?'#080a0d':'var(--t-secondary)', border:`1px solid ${activeType===t?(TYPE_COLORS[t]||'var(--gold)'):'var(--border)'}`, transition:'all 0.15s' }}>{t||'All Types'}</button>
              ))}
              {domains.length > 0 && (
                <>
                  <div style={{ width:1, height:18, background:'var(--border)', margin:'0 4px' }}/>
                  {domains.map(d => (
                    <button key={d.id} onClick={()=>setDomain(activeDomain===d.slug?'':d.slug)} style={{ padding:'6px 12px', borderRadius:20, fontSize:'0.75rem', cursor:'pointer', fontWeight:activeDomain===d.slug?600:400, background:activeDomain===d.slug?'rgba(232,164,58,0.12)':'var(--bg-2)', color:activeDomain===d.slug?'var(--gold)':'var(--t-secondary)', border:`1px solid ${activeDomain===d.slug?'rgba(232,164,58,0.35)':'var(--border)'}`, transition:'all 0.15s' }}>{d.name}</button>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <section style={{ padding:'clamp(40px,6vw,80px) 0', minHeight:'60vh' }}>
        <div className="container">
          {loading ? (
            <div style={{ textAlign:'center', padding:80, color:'var(--t-muted)' }}>Loading…</div>
          ) : tab === 'programs' ? (
            programs.length === 0 ? (
              <div style={{ textAlign:'center', padding:80 }}>
                <div style={{ fontSize:'2rem', marginBottom:12, opacity:0.3 }}>◐</div>
                <div style={{ color:'var(--t-muted)', marginBottom:16 }}>No programs found</div>
                <button onClick={()=>{setType('');setDomain('');}} className="btn btn-outline" style={{ margin:'0 auto' }}>Clear Filters</button>
              </div>
            ) : (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px, 1fr))', gap:20 }}>
                {programs.map((p,i) => <ProgramCard key={p.id} program={p} onEnroll={setEnrolling} idx={i}/>)}
              </div>
            )
          ) : (
            internships.length === 0 ? (
              <div style={{ textAlign:'center', padding:80 }}>
                <div style={{ fontSize:'2rem', marginBottom:12, opacity:0.3 }}>◉</div>
                <div style={{ color:'var(--t-muted)', marginBottom:16 }}>No open internships right now</div>
                <Link to="/contact?type=volunteer" className="btn btn-outline" style={{ margin:'0 auto' }}>Express Interest →</Link>
              </div>
            ) : (
              <>
                <div style={{ marginBottom:24, fontSize:'0.82rem', color:'var(--t-muted)' }}>
                  {internships.length} open internship{internships.length!==1?'s':''} — click to apply
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px, 1fr))', gap:20 }}>
                  {internships.map((i,idx) => <InternshipCard key={i.id} internship={i} idx={idx}/>)}
                </div>
              </>
            )
          )}
        </div>
      </section>

      {enrolling && <EnrollModal program={enrolling} onClose={()=>setEnrolling(null)}/>}
    </div>
  );
}